/** Backend key for Board Governance studio data scope. */
export const STUDIO_SCOPE_BACKEND_KEY = 'studio.board_governance';

/** Level-1 backend keys whose tab access may inherit from level-2 children. */
export const LEVEL_1_BACKEND_KEYS = ['business', 'studio', 'publishing'] as const;

/** pageId → backend permission_key (level-1 tabs + level-2 tabs). */
export const PAGE_ID_TO_BACKEND_KEY: Record<string, string> = {
  business: 'business',
  business_executiveSummary: 'business.executive_summary',
  business_overallFinancials: 'business.overall_financials',
  studio: 'studio',
  studio_portfolio: 'studio.portfolio_overview',
  studio_studios: 'studio.studios',
  studio_liveTitles: 'studio.live_titles',
  studio_inDevProjects: 'studio.in_dev_projects',
  studio_boardGovernance: STUDIO_SCOPE_BACKEND_KEY,
  publishing: 'publishing',
  publishing_overview: 'publishing.overview',
  publishing_mktcost: 'publishing.mkt_cost',
  publishing_headcount: 'publishing.publishing_business_headcount',
  publishing_project: 'publishing.publishing_project',
};

/** Level-3 button pageId → parent backend permission_key (inherits writeable). */
export const PAGE_ID_TO_PARENT_BACKEND_KEY: Record<string, string> = {
  business_totalGrossRevenue_notes: 'business.executive_summary',
  business_totalProfit_notes: 'business.executive_summary',
  business_weeklyProjectHighlight: 'business.executive_summary',
  studio_overview_grossRevenueTrend: 'studio.portfolio_overview',
  studio_overview_notes: 'studio.portfolio_overview',
  studio_overview_pipelineCalendar: 'studio.portfolio_overview',
  'studio_studios_Appendix&Notes': 'studio.studios',
  studio_studios_pipelineCalendar: 'studio.studios',
  studio_liveTitles_notes: 'studio.live_titles',
  studio_liveTitles_liveVisualizationAll: 'studio.live_titles',
  studio_inDevProjects_headcount: 'studio.in_dev_projects',
  studio_inDevProjects_lifeCycleFinancialsForecast: 'studio.in_dev_projects',
  studio_inDevProjects_GRManagement: 'studio.in_dev_projects',
  studio_inDevProjects_notes: 'studio.in_dev_projects',
  studio_inDevProjects_inDevProjectsAll: 'studio.in_dev_projects',
  publishing_overview_grossRevenueByProject: 'publishing.overview',
  publishing_overview_decisionPointsList: 'publishing.overview',
  publishing_overview_notes: 'publishing.overview',
  publishing_mktcost_mktCostByProject: 'publishing.mkt_cost',
  publishing_mktcost_mktBudgetApplicationCalendar: 'publishing.mkt_cost',
  publishing_mktcost_notes: 'publishing.mkt_cost',
  publishing_headcount_notes: 'publishing.publishing_business_headcount',
  publishing_headcount_projectHeadcount: 'publishing.publishing_business_headcount',
  'publishing_headcount_Appendix&Notes': 'publishing.publishing_business_headcount',
  publishing_project_publishingReviewUpdates: 'publishing.publishing_project',
  'publishing_project_Appendix&Notes': 'publishing.publishing_project',
};

/**
 * Reserved Level-3 Board Governance mappings.
 *
 * Board Governance is currently a Level-2 Studio Management tab, so its active
 * mapping lives in PAGE_ID_TO_BACKEND_KEY. Keep these examples for a future
 * embedded Level-3 module without making them part of today's permission flow.
 */
export const PAGE_ID_TO_SCOPE_BACKEND_KEY: Record<string, string> = {
  // studio_overview_boardGovernance: STUDIO_SCOPE_BACKEND_KEY,
  // studio_studios_boardGovernance: STUDIO_SCOPE_BACKEND_KEY,
};

/** Page IDs whose data is restricted by studio_ids on studio.board_governance. */
export const BOARD_GOVERNANCE_PAGE_IDS = [
  'studio_boardGovernance',
  // Future Level-3 Board Governance modules can be added here when restored:
  // 'studio_overview_boardGovernance',
  // 'studio_studios_boardGovernance',
] as const;

export const resolveBackendKey = (pageId: string): string | undefined => (
  PAGE_ID_TO_BACKEND_KEY[pageId]
  ?? PAGE_ID_TO_SCOPE_BACKEND_KEY[pageId]
  ?? PAGE_ID_TO_PARENT_BACKEND_KEY[pageId]
);

export const getLevel2BackendKeys = (level1Key: string): string[] => (
  Object.values(PAGE_ID_TO_BACKEND_KEY).filter(key => key.startsWith(`${level1Key}.`))
);

/** L2/L3 backend key → level-1 parent (e.g. studio.portfolio_overview → studio). */
export const getParentBackendKey = (backendKey: string): string | undefined => {
  if (isLevel1BackendKey(backendKey)) {
    return undefined;
  }

  return LEVEL_1_BACKEND_KEYS.find(level1Key => (
    backendKey.startsWith(`${level1Key}.`)
  ));
};

export const isLevel1BackendKey = (backendKey: string): boolean => (
  (LEVEL_1_BACKEND_KEYS as readonly string[]).includes(backendKey)
);

export const isBoardGovernancePageId = (pageId: string): boolean => (
  (BOARD_GOVERNANCE_PAGE_IDS as readonly string[]).includes(pageId)
);

export const isBoardGovernanceScopeKey = (backendKey: string): boolean => (
  backendKey === STUDIO_SCOPE_BACKEND_KEY
);
